/* eslint-disable import/prefer-default-export */

import type { State } from '../configureStore'
import { Rules as ActionTypes } from '../actionTypes'
import {
  defaultState,
  FailedObject,
  RuleItem
} from '../reducers/rules/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import * as intentproto from '@src/endpoints/services/intentMgr.service'
import { getAccountNameById } from '@src/utils/accounts'
import { filter, includes, isEmpty, map, find } from 'lodash'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import {
  AlertSeverityEnum,
  APIResponse,
  getGuardrails,
  GuardrailSummary,
  GuardrailTypeEnum
} from '@src/endpoints/openapis/saas-cam-guardrails'
import { AlertSeverityEnum as CommonAlertSeverityEnum } from '@src/endpoints/services/commonHdr.service'
import { AlertTypeEnum } from '@src/endpoints/services/intentMgr.service'
import * as AlertApi from '@src/endpoints/openapis/saas-cam-alerts/api'
import {
  GetAlertsRequest,
  GetAlertsSummaryRequest
} from '@src/endpoints/openapis/saas-cam-alerts/api'
import type { Summary } from '@src/endpoints/openapis/saas-cam-alerts/models'
import { getTypeFromUri } from '@src/utils/url.utils'
import { GuardrailSummaryModel } from '../reducers/guardrails/models'

// Action Creators
export type ActionTypes =
  | getRulesAction
  | getRuleDetailsAction
  | requestGetRulesPendingAction
  | requestGetRulePendingAction
  | changeStateAction
  | getFailedCountsRecordsAction

export const changeState = ReduxHelpers.Common.Actions.changeState(
  ActionTypes.CHANGE_STATE,
  defaultState
)

type changeStateAction = ReturnType<typeof changeState>

export const getRules = (
  rules: intentproto.GroupRuleInfo[] = [],
  total = 0,
  failing = 0,
  passing = 0,
  title: string
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_RULES, {
    rules,
    total,
    passing,
    failing,
    title
  })
type getRulesAction = ReturnType<typeof getRules>

export interface RuleDetails {
  name?: string
  description?: string
  remediation?: string
  audit?: string
  remediate?: boolean
  category?: string
  query?: string
}

export const getRuleDetails = (
  rule: RuleDetails,
  failedObjects: FailedObject[]
) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_RULE_DETAILS, {
    rule,
    failedObjects
  })
type getRuleDetailsAction = ReturnType<typeof getRuleDetails>

export const requestGetRulesPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_RULES_PENDING, {
    isPending
  })
type requestGetRulesPendingAction = ReturnType<typeof requestGetRulesPending>

export const requestGetRulePending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    ActionTypes.GET_RULE_DETAILS_PENDING,
    {
      isPending
    }
  )
type requestGetRulePendingAction = ReturnType<typeof requestGetRulePending>

export const getFailedCountsRecords = (failedByRuleId: Summary[]) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.FAILED_COUNTS_RECORDS, {
    failedByRuleId
  })
type getFailedCountsRecordsAction = ReturnType<typeof getFailedCountsRecords>

interface OnGetRulesParams {
  region?: string
  object_uri?: string
}

export function onGetRules(
  { region, uri }: { region: string; uri: string },
  callback?
) {
  return (dispatch: any, getState: () => State) => {
    const params = {} as OnGetRulesParams
    if (!isEmpty(region)) {
      params.region = region
    }
    if (!isEmpty(uri)) {
      params.object_uri = uri
    }
    dispatch(
      changeState({
        isPending: true,
        list: [],
        totalCount: 0,
        failedCount: 0,
        passedCount: 0
      })
    )
    intentproto.IntentMgr.GetRules({ params })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestGetRulesPending(false))
          },
          true
        )
      )
      .then(response => {
        const { rules, total_tests, failing_tests, passing_tests } = response
        dispatch(
          getRules(
            (rules || []).map(x => ({ ...x, name: x.name || x.id })),
            total_tests,
            failing_tests,
            passing_tests,
            region
          )
        )
        if (typeof callback === 'function') callback()
      })
      .catch(ex => {
        dispatch(requestGetRulesPending(false))
        return ex
      })
  }
}

export function getGuardrailsRule(
  { region, ids }: { region: string; ids?: string[] },
  callbackSuccess?: () => void
) {
  return (dispatch: any, getState: () => State) => {
    dispatch(
      changeState({
        isPending: true,
        list: [],
        totalCount: 0,
        failedCount: 0,
        passedCount: 0
      })
    )
    getGuardrails({ enabledOnly: true })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestGetRulePending(false))
          },
          true
        )
      )
      .then(res => {
        let list = res || []
        if (ids?.length > 0) {
          list = filter(
            list || [],
            x => includes(ids, x.id) || includes(ids, x.autoIntentId)
          )
        }
        dispatch(getRules(parseGuardrailsToRules(list || []), 0, 0, 0, region))
        if (callbackSuccess) {
          callbackSuccess()
        }
      })
      .catch(ex => {
        dispatch(requestGetRulesPending(false))
        return ex
      })
  }
}

export function convertSeverityEnum(
  severity: AlertSeverityEnum
): CommonAlertSeverityEnum {
  switch (severity) {
    case AlertSeverityEnum.Low: {
      return CommonAlertSeverityEnum.ALERT_SEVERITY_LOW
    }
    case AlertSeverityEnum.Medium: {
      return CommonAlertSeverityEnum.ALERT_SEVERITY_MEDIUM
    }
    case AlertSeverityEnum.High: {
      return CommonAlertSeverityEnum.ALERT_SEVERITY_HIGH
    }
    default: {
      return severity
    }
  }
}

export function convertAlertType(type: GuardrailTypeEnum): AlertTypeEnum {
  switch (type) {
    case GuardrailTypeEnum.Autocomplete:
    case GuardrailTypeEnum.Function:
    default: {
      return AlertTypeEnum.ALERT_TYPE_GUARDRAIL
    }
  }
}

function parseGuardrailsToRules(
  data: GuardrailSummary[]
): intentproto.GroupRuleInfo[] {
  return map(data, x => ({
    id: x?.id,
    autoIntentId: x?.autoIntentId,
    name: x?.name,
    severity: convertSeverityEnum(x?.alertSeverity),
    alert_type: convertAlertType(x?.type),
    failed_count: x?.failedObjectsCount,
    last_status_change: x?.lastUpdatedBy,
    query: x?.query,
    category:
      x?.folders?.length > 0
        ? map(x?.folders || [], f => f?.name).join(', ')
        : null,
    last_alert_date: x?.lastAlertDateTime
  }))
}

const parseRegion = (region: string) =>
  !isEmpty(region) && region !== '0' ? region : 'Global'

interface OnGetRuleDetailsOptions {
  ruleId?: string
  objectUri?: string
  region?: string
  showDismissed?: boolean
  showOpenOnly?: boolean
  shallowFetch: boolean
}

export function onGetRuleDetails({
  ruleId,
  objectUri,
  region,
  showDismissed = false,
  showOpenOnly,
  shallowFetch = false
}: OnGetRuleDetailsOptions) {
  return (dispatch: any, getState: () => State) => {
    const params: intentproto.GetRuleReq = {
      rule_id: ruleId,
      show_dismissed: showDismissed,
      show_open_only: showOpenOnly
    }
    if (!isEmpty(region)) {
      params.region = region
    }
    if (!isEmpty(objectUri)) {
      params.object_uri = objectUri
    }

    if (!shallowFetch) {
      dispatch(
        changeState({
          isPending: true,
          ruleDetails: {},
          failedObjects: []
        })
      )
    } else {
      dispatch(requestGetRulePending(true))
    }

    intentproto.IntentMgr.GetRule({ params })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestGetRulePending(false))
          },
          true
        )
      )
      .then(response => {
        const { auth } = getState()
        const { tenant } = auth
        const { account_info = [] } = tenant || {}
        const { rule_details, failed_objects = [] } = response
        const convObjects = failed_objects.map<FailedObject>(obj => ({
          ...obj,
          account: getAccountNameById(obj.account_id, account_info),
          region: parseRegion(obj.region)
        }))

        dispatch(getRuleDetails(rule_details, convObjects))
      })
      .catch(ex => {
        dispatch(requestGetRulePending(false))
        return ex
      })
  }
}

export function onGetRuleDetailsNew(reqParams: GetAlertsRequest, uri?: string) {
  return (dispatch: any, getState: () => State) => {
    const { rule, guardrails } = getState()
    const { list } = rule
    const { guardrailsList = [] as GuardrailSummaryModel[] } = guardrails

    dispatch(
      changeState({
        isPending: true,
        list: [],
        totalCount: 0,
        failedCount: 0,
        passedCount: 0
      })
    )
    AlertApi.getAlerts(reqParams)
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestGetRulesPending(false))
          },
          true
        )
      )
      .then(response => {
        const { auth } = getState()
        const { tenant } = auth
        const { account_info = [] } = tenant || {}
        const convObjects = response.map(obj => ({
          referenceType: obj.referenceType,
          account: getAccountNameById(obj.accountId, account_info),
          account_id: obj.accountId,
          region: parseRegion(obj.region),
          object_uri: obj.objectURI,
          alert_id: obj.id,
          type: getTypeFromUri(obj.objectURI),
          time: obj.createdTime,
          monitor_id: obj.referenceId,
          name: obj.objectName,
          dismissed: !!obj.dismissedBy,
          dismissed_time: obj.dismissedTime
        }))
        const ruleId = response[0]?.referenceId
        const ruleFound = find(list, r => r?.id === ruleId) as RuleItem
        const gRuleFound = find(
          guardrailsList,
          r => r?.id === ruleId
        ) as RuleItem

        const guardrailInfo: RuleDetails = isEmpty(gRuleFound)
          ? {}
          : {
              name: gRuleFound?.name,
              category: gRuleFound?.category,
              query: gRuleFound?.query
            }
        const alertInfo: RuleDetails = isEmpty(ruleFound)
          ? {}
          : {
              name: ruleFound?.name,
              category: ruleFound?.category,
              query: ruleFound?.query
            }

        if (!isEmpty(uri)) {
          dispatch(
            getRuleDetails(
              { ...guardrailInfo, ...alertInfo },
              filter(convObjects || [], x => x.object_uri === uri) as any
            )
          )
        } else {
          dispatch(
            getRuleDetails(
              { ...guardrailInfo, ...alertInfo },
              convObjects as any
            )
          )
        }
      })
      .catch(ex => {
        dispatch(requestGetRulesPending(false))
        return ex
      })
  }
}

export function onGetFailedCountsByRuleId(
  params?: GetAlertsSummaryRequest,
  callback?: (data?: APIResponse<Summary[]>) => void
) {
  return (dispatch: any, getStore: () => State) => {
    dispatch(requestGetRulesPending(true))
    AlertApi.getAlertsSummary({
      ...params
    })
      .then(res =>
        defaultHandleStatus(
          res,
          () => {
            dispatch(requestGetRulesPending(false))
          },
          true
        )
      )
      .then(res => {
        dispatch(getFailedCountsRecords(res))
        if (callback) callback(res)
        dispatch(requestGetRulesPending(false))
      })
      .catch((res: Response) => dispatch(requestGetRulesPending(false)))
  }
}
